// FAQ tile (companion) — full consult + AUTHOR surface: list · search · add / edit /
import { confirmDiscard } from "./guard";
// delete a Q&A (desktop-PWA parity). Reads stay same-origin (/api/faq GET, rscan_sid
// cookie); writes proxy S2S to the Outlook /api/faq engine so the question EMBEDDING
// stays single-source there. Editor on the same form chassis as Tasks/Minutes.
import { toast } from "./app";
import { loadSnap, saveSnap, skeletonHtml } from "./cache";
import { confirmDanger } from "./confirm";

const $ = <T extends Element = HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);

type Faq = {
  id: number | string; // Neon returns BIGINT as a string
  question: string;
  answer: string;
  urls: string[] | null;
  language: string | null;
  is_active: boolean;
  times_used: number | null;
  scope?: "personal" | "company"; // ORG-DATA-OWNERSHIP: company = org-shared, READ-ONLY on the phone
  mine?: boolean;
  creator_name?: string | null;
};
type Company = { enabled: boolean; orgName: string | null };

let all: Faq[] = [];
let company: Company = { enabled: false, orgName: null };
let loaded = false;
let editId: string | null = null;
let urls: string[] = []; // the editor's link chips

const esc = (s: unknown): string =>
  String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
const snippet = (s: string, n = 130): string => {
  const t = s.replace(/\s+/g, " ").trim();
  return t.length > n ? t.slice(0, n - 1) + "…" : t;
};

// ── data ──
// Stale-while-revalidate: paint the last-known list (or a skeleton on a true first run)
// immediately, then fetch and re-render. The screen is never blanked while loading.
export async function loadFaq(force = false): Promise<void> {
  if (loaded && !force) { renderList(); return; }
  let painted = loaded;
  if (!loaded) {
    const snap = loadSnap<Faq[]>("faq");
    const snapCo = loadSnap<Company>("faq-company");
    if (snapCo) company = snapCo;
    if (snap) { all = snap; painted = true; renderList(); }
    else { const list = $("#faqList"); if (list) list.innerHTML = skeletonHtml(); }
  }
  try {
    const r = await fetch("/api/faq", { credentials: "include" });
    if (r.status === 401) { renderEmpty("This phone isn't linked. Reconnect from Outlook to load your FAQ."); return; }
    if (!r.ok) throw new Error(String(r.status));
    const j = (await r.json()) as { items?: Faq[]; company?: Company };
    all = j.items || [];
    if (j.company) { company = j.company; saveSnap("faq-company", company); }
    loaded = true;
    saveSnap("faq", all);
    renderList();
  } catch {
    if (!painted) renderEmpty("Couldn't load your FAQ. Check your connection and try again.");
  }
}

function renderEmpty(msg: string): void {
  const list = $("#faqList");
  const empty = $("#faqEmpty");
  if (list) list.innerHTML = "";
  if (empty) { empty.textContent = msg; empty.style.display = "block"; }
  const count = $("#topCount"); if (count) { count.textContent = ""; count.hidden = true; }
}

// ── list (flat, searchable) ──
function renderList(): void {
  const term = ($("#faqSearch") as HTMLInputElement | null)?.value.trim().toLowerCase() || "";
  const items = term
    ? all.filter((q) => `${q.question} ${q.answer}`.toLowerCase().includes(term))
    : all;

  // Active-screen gate: the Home-boot prefetch renders in the background (see contacts.ts).
  const count = $("#topCount");
  if (count && $("#screen-faq")?.classList.contains("active")) { count.textContent = all.length ? String(all.length) : ""; count.hidden = !all.length; }

  const empty = $("#faqEmpty");
  const list = $("#faqList");
  if (!list) return;
  if (!items.length) {
    list.innerHTML = "";
    if (empty) { empty.textContent = all.length ? "No questions match." : "No FAQ yet — add your first with + New."; empty.style.display = "block"; }
    return;
  }
  if (empty) empty.style.display = "none";

  list.innerHTML = items
    .map((q) =>
      `<button class="faq-row${q.is_active ? "" : " off"}" data-id="${q.id}">` +
      `<span class="faq-q-badge"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.1 9a2.9 2.9 0 0 1 5.5 1c0 2-2.9 2.9-2.9 2.9"/><path d="M12 17h.01"/></svg></span>` +
      `<span class="faq-row-main"><span class="faq-row-q">${esc(q.question)}${q.scope === "company" ? ` <span class="vf-chip-company">${esc(company.orgName || "Company")}</span>` : ""}</span><span class="faq-row-a">${esc(snippet(q.answer))}</span></span>` +
      `<span class="crow-chev"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg></span>` +
      `</button>`,
    )
    .join("");
}

// ── editor (read + edit + new in one overlay, Tasks-style) ──
function renderUrlChips(): void {
  const host = $("#faqLinks");
  if (!host) return;
  const ro = editId != null && all.find((x) => String(x.id) === editId)?.scope === "company";
  host.innerHTML = urls
    .map((u, i) =>
      `<span class="faq-link-chip"><span class="t">${esc(u.replace(/^https?:\/\//, ""))}</span>` +
      (ro ? "" : `<button type="button" data-rmurl="${i}" title="Remove link"><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button>`) +
      `</span>`,
    )
    .join("");
  (host as HTMLElement).style.display = urls.length ? "flex" : "none";
}
function commitTypedUrl(): void {
  const inp = $("#faqfUrl") as HTMLInputElement | null;
  const v = inp?.value.trim() || "";
  if (!v) return;
  urls.push(v);
  if (inp) inp.value = "";
  renderUrlChips();
}

function openEditor(q: Faq | null): void {
  editId = q ? String(q.id) : null;
  urls = (q?.urls || []).filter(Boolean);
  // Company entries are READ-ONLY on the phone (ORG-DATA-OWNERSHIP): the governance matrix
  // (edit = author + admin, delete/demote = admin) plus the Owner control live on desktop.
  const ro = q?.scope === "company";
  const title = $("#faqEdTitle"); if (title) title.textContent = ro ? `${company.orgName || "Company"} FAQ` : q ? "FAQ" : "New FAQ";
  const del = $("#faqEdDelete") as HTMLElement | null; if (del) { del.hidden = !q || ro; del.style.display = !q || ro ? "none" : ""; }
  const copy = $("#faqCopyBtn") as HTMLElement | null; if (copy) copy.hidden = !q;
  const hint = $("#faqOwnerHint") as HTMLElement | null;
  if (hint) {
    hint.hidden = !ro;
    if (ro) hint.textContent = `Shared across ${company.orgName || "your company"}${q?.creator_name ? ` — added by ${q.creator_name}` : ""}. View-only here; edit it in the desktop app.`;
  }
  ($("#faqfQ") as HTMLInputElement | null)!.value = q?.question || "";
  ($("#faqfA") as HTMLTextAreaElement | null)!.value = q?.answer || "";
  ($("#faqfUrl") as HTMLInputElement | null)!.value = "";
  ($("#faqfQ") as HTMLInputElement | null)!.disabled = ro;
  ($("#faqfA") as HTMLTextAreaElement | null)!.disabled = ro;
  const urlInp = $("#faqfUrl") as HTMLInputElement | null; if (urlInp) urlInp.style.display = ro ? "none" : "";
  const save = $("#faqSave") as HTMLElement | null; if (save) { save.hidden = ro; save.style.display = ro ? "none" : ""; } // CSS display rules beat [hidden]
  renderUrlChips();
  $("#faqDetail")?.classList.add("open");
  setTimeout(() => { faqOpenSnap = JSON.stringify({ question: ($("#faqfQ") as HTMLInputElement | null)?.value.trim() || "", answer: ($("#faqfA") as HTMLTextAreaElement | null)?.value.trim() || "", urls }); }, 0);
}
function closeEditor(): void { $("#faqDetail")?.classList.remove("open"); editId = null; }

/** Home-search result tap → straight into the editor (after loadFaq's snapshot paint). */
export function openFaqById(id: string): void {
  const q = all.find((x) => String(x.id) === id);
  if (q) openEditor(q);
}

// SAVE-CANON C2/C4: back on an EXISTING FAQ auto-saves when changed; back on a dirty NEW one
// asks the shared Discard confirm.
let faqOpenSnap = "";
async function flushCloseFaq(): Promise<void> {
  commitTypedUrl();
  const question = ($("#faqfQ") as HTMLInputElement | null)?.value.trim() || "";
  const answer = ($("#faqfA") as HTMLTextAreaElement | null)?.value.trim() || "";
  const snap = JSON.stringify({ question, answer, urls });
  if (editId) {
    const ro = ($("#faqfQ") as HTMLInputElement | null)?.disabled; // org read-only rows never save
    if (!ro && snap !== faqOpenSnap && question && answer) { await save(); return; } // save() closes + reloads
  } else if (question || answer) {
    if (!(await confirmDiscard("this FAQ"))) return;
  }
  closeEditor();
}

async function save(): Promise<void> {
  commitTypedUrl(); // a typed-but-not-committed link still counts
  const question = ($("#faqfQ") as HTMLInputElement | null)?.value.trim() || "";
  const answer = ($("#faqfA") as HTMLTextAreaElement | null)?.value.trim() || "";
  if (!question) { toast("Add the question"); return; }
  if (!answer) { toast("Add the answer"); return; }
  const btn = $("#faqSave") as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = "Saving…"; }
  try {
    const r = await fetch("/api/faq", {
      method: editId ? "PUT" : "POST",
      headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify(editId ? { id: Number(editId), question, answer, urls } : { question, answer, urls }),
    });
    const j = (await r.json().catch(() => ({}))) as { item?: Faq; error?: string };
    if (!r.ok || !j.item) { toast("Couldn't save — try again"); return; }
    closeEditor();
    toast(editId ? "FAQ updated" : "FAQ added");
    void loadFaq(true);
  } catch { toast("Couldn't save — check your connection"); }
  finally {
    if (btn) { btn.disabled = false; btn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg> Save`; }
  }
}

async function removeFaq(): Promise<void> {
  if (!editId) return;
  const q = all.find((x) => String(x.id) === editId);
  const yes = await confirmDanger({
    title: "Delete this FAQ?",
    body: `"${snippet(q?.question || "this question", 80)}" stops being used in replies on every device. This cannot be undone.`,
    cta: "Delete FAQ",
  });
  if (!yes) return;
  try {
    const r = await fetch(`/api/faq?id=${encodeURIComponent(editId)}`, { method: "DELETE", credentials: "include" });
    if (!r.ok) { toast("Couldn't delete — try again"); return; }
    closeEditor();
    toast("FAQ deleted");
    void loadFaq(true);
  } catch { toast("Couldn't delete — check your connection"); }
}

async function copyAnswer(): Promise<void> {
  const answer = ($("#faqfA") as HTMLTextAreaElement | null)?.value || "";
  if (!answer) return;
  try { await navigator.clipboard.writeText(answer); toast("Answer copied"); } catch { /* clipboard blocked */ }
}

// ── wiring ──
export function initFaq(): void {
  const search = $("#faqSearch") as HTMLInputElement | null;
  const onSearch = (): void => { renderList(); const cl = $("#faqClear"); if (cl) cl.hidden = !search?.value; };
  search?.addEventListener("input", onSearch);
  $("#faqClear")?.addEventListener("click", () => { if (search) search.value = ""; onSearch(); search?.focus(); });
  $("#faqNew")?.addEventListener("click", () => openEditor(null));
  $("#faqDetailBack")?.addEventListener("click", () => void flushCloseFaq());
  $("#faqCopyBtn")?.addEventListener("click", () => void copyAnswer());
  $("#faqSave")?.addEventListener("click", () => void save());
  $("#faqEdDelete")?.addEventListener("click", () => void removeFaq());
  const urlInp = $("#faqfUrl") as HTMLInputElement | null;
  urlInp?.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commitTypedUrl(); } });
  urlInp?.addEventListener("blur", commitTypedUrl);
  $("#faqLinks")?.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-rmurl]");
    if (b) { urls.splice(Number(b.getAttribute("data-rmurl")), 1); renderUrlChips(); }
  });
  $("#faqList")?.addEventListener("click", (e) => {
    const row = (e.target as HTMLElement).closest<HTMLElement>(".faq-row");
    if (row) { const q = all.find((x) => String(x.id) === (row.getAttribute("data-id") || "")); if (q) openEditor(q); }
  });
}
